import React, { useCallback, useRef, useState } from 'react';

import { components } from 'react-select';
import AsyncSelect from 'react-select/async';

import { api, lang, remCss, rgba, scssVariables } from 'src/helpers';
import SVGIcon, { BASE_ICONS } from '../svg';
import LANG_CONST from 'src/helpers/lang/const';

export const AutoCompleteField = ({
  template,
  label,
  name,
  value,
  onChange,
  placeholder,
  autocompleteParams = {},
  autocompleteApi,
  invalid = {},
  width = '100%',
  disabled = false,
  isClearable = true,
  isMultiple = false,
  isOptional = false,
  allowNew = false
}) => {
  let error = invalid && invalid[name];
  if (error?.includes('required'))
    error = lang.t(LANG_CONST.validation.required, { label: lang.t(label) });

  const [typingTimer, setTypingTimer] = useState();
  const inputValueRef = useRef({});
  const selectRef = useRef();
  const styleDefault = {
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: remCss(8),
      svg: {
        width: remCss(20),
        height: remCss(20)
      }
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: remCss(8),
      svg: {
        width: remCss(20),
        height: remCss(20)
      }
    }),
    valueContainer: (s) => ({
      ...s,
      padding: `${remCss(2)}px ${remCss(8)}px`
    })
  };

  const styles = {
    0: {
      container: (s) => ({ ...s, width: width }),
      control: (s) => ({
        ...s,
        ':hover': {
          border: 'none'
        },

        border: 'none',
        paddingTop: remCss(5),
        paddingBottom: remCss(5),
        paddingLeft: remCss(4),
        paddingRight: remCss(4)
      }),
      option: (s, { isSelected }) => ({
        ...s,
        backgroundColor: isSelected
          ? rgba(scssVariables['colors-mc-primary'], 0.5)
          : scssVariables['colors-mc-white']
      }),
      ...styleDefault
    },
    1: {
      container: (s) => ({ ...s, width: width }),
      control: (s) => ({
        ...s,
        ':hover': {
          border: 'none'
        },
        border: 'none',
        padding: 0
      }),
      ...styleDefault
    },
    2: {
      container: (s) => ({ ...s, width: width }),
      control: (s) => ({
        ...s,
        border: 'none',
        backgroundColor: 'transparent',
        cursor: disabled ? 'not-allowed' : 'default',
        ':hover': {
          border: 'none'
        },
        paddingTop: remCss(8),
        paddingBottom: remCss(8),
        paddingLeft: remCss(4),
        paddingRight: remCss(4)
      }),
      ...styleDefault
    },
    3: {
      container: (s) => ({ ...s, width: width }),
      control: (s) => ({
        ...s,
        boxShadow: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: disabled ? 'not-allowed' : 'default',
        ':hover': {
          border: 'none'
        },
        paddingTop: remCss(6),
        paddingBottom: remCss(6),
        paddingLeft: remCss(7),
        paddingRight: remCss(7)
      }),
      multiValue: (styles) => ({
        ...styles,
        backgroundColor: 'white'
      }),
      ...styleDefault
    }
  };
  styles[5] = styles[0];
  styles[6] = styles[0];
  const placeholderTxt = (lang.t(placeholder) || lang.t('Search')) + '...';
  const loadOptions = useCallback(
    (inputValue, cb) => {
      inputValueRef.current = { value: inputValue, canChanged: true };
      if (typingTimer) clearTimeout(typingTimer);

      setTypingTimer(
        setTimeout(() => {
          api('autocomplete/' + autocompleteApi, {
            limit: 10,
            query: inputValue,
            ...autocompleteParams
          }).then(({ error, data }) => {
            if (error === 'cancel') return;
            let items = Array.isArray(data) ? data : [];
            if (error) console.warn('loadAutocomplete err', error);
            cb(items);
          });
        }, 200)
      );
    },
    [autocompleteApi, autocompleteParams, typingTimer]
  );

  const onBlur = useCallback(() => {
    if (allowNew && inputValueRef.current.canChanged) {
      onChange(name, inputValueRef.current.value);
    }
  }, [allowNew, name, onChange]);

  return (
    <div className={'mc-select-field-group template-' + template}>
      {label && (
        <label>
          {lang.t(label)}{' '}
          {isOptional && <span className={'mc-label-414'}>({lang.t(LANG_CONST.optional)})</span>}
        </label>
      )}
      <AsyncSelect
        ref={(ref) => (selectRef.current = ref)}
        noOptionsMessage={({ inputValue }) => {
          return inputValue && inputValue.length > 0 ? 'No results' : '';
        }}
        key={Object.values(autocompleteParams).join('_')}
        className='basic-single'
        classNamePrefix='select'
        cacheOptions={false}
        value={value ? { value, label: value.toString().split('_')[0] } : null}
        loadOptions={loadOptions}
        defaultOptions={true}
        placeholder={placeholderTxt}
        onMenuOpen={() => {
          setTimeout(() => {
            const selected = selectRef.current?.menuListRef.getElementsByClassName(
              'select__option--is-selected'
            );

            if (selected.length)
              selected[0]?.scrollIntoView({
                behavior: 'instant',
                block: 'center'
              });
          }, 100);
        }}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: scssVariables['colors-mc-white']
          }
        })}
        onBlur={onBlur}
        components={{
          DropdownIndicator: (props) => {
            return (
              <components.DropdownIndicator {...props}>
                <SVGIcon
                  iconname={BASE_ICONS.SEARCH}
                  strokeWidthIcon={0.5}
                  width={20}
                  fill={scssVariables['colors-mc-text']}
                />
              </components.DropdownIndicator>
            );
          }
          // ClearIndicator: () => null
        }}
        isMultiple={isMultiple}
        isClearable={isClearable}
        onChange={(a) => {
          inputValueRef.current.canChanged = false;
          onChange(name, a && (isMultiple ? a.map((item) => item.value) : a.value), a);
        }}
        menuPlacement={'auto'}
        styles={styles[template]}
        menuPosition={'fixed'}
      />
      {error && <p className='mc-form-invalid-text'>{error}</p>}
    </div>
  );
};
export default AutoCompleteField;
